import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

/**
 * Compact OTP Verification Component for DC/ADC Approval
 * Combined comments and OTP verification in one screen
 * Automatically triggers approval after successful OTP verification
 */
const OTPVerificationCompact = ({ permitId, purpose = 'permit_approval', skipVerification = false, reviewComments: initialComments = '', onSuccess, onCancel, isLoading }) => {
  // Use cancellation OTP endpoints when purpose is permit_cancellation
  const otpEndpointPrefix = purpose === 'permit_cancellation' ? 'cancel' : 'permit';
  const [comments, setComments] = useState(initialComments);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpId, setOtpId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];
  const otpGeneratedRef = useRef(false);

  // Generate OTP on component mount (only once)
  useEffect(() => {
    if (!otpGeneratedRef.current) {
      otpGeneratedRef.current = true;
      generateOTP();
    }
  }, []);

  // Timer for resend countdown (60 seconds)
  useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  // Generate OTP
  const generateOTP = async () => {
    setIsGenerating(true);
    setVerificationError(null);

    try {
      const response = await api.post(`/otp/generate/${otpEndpointPrefix}/${permitId}`);

      if (response.data.success) {
        setOtpId(response.data.otpId);
        setExpiresAt(response.data.expiresAt);
        setResendTimer(60);
        toast.success(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to generate OTP';
      toast.error(message);
      setVerificationError(message);

      if (error.response?.data?.secondsRemaining) {
        setResendTimer(Math.min(error.response.data.secondsRemaining, 60));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Resend OTP
  const resendOTP = async () => {
    if (resendTimer > 0) return;

    setIsResending(true);
    setVerificationError(null);
    setOtpCode(['', '', '', '', '', '']);
    inputRefs[0].current?.focus();

    try {
      const response = await api.post(`/otp/resend/${otpEndpointPrefix}/${permitId}`);

      if (response.data.success) {
        setOtpId(response.data.otpId);
        setExpiresAt(response.data.expiresAt);
        setResendTimer(60);
        toast.success(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to resend OTP';
      toast.error(message);
      setVerificationError(message);

      if (error.response?.data?.secondsRemaining) {
        setResendTimer(Math.min(error.response.data.secondsRemaining, 60));
      }
    } finally {
      setIsResending(false);
    }
  };

  // Handle OTP input change
  const handleOTPChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newOTP = [...otpCode];
    newOTP[index] = value;
    setOtpCode(newOTP);
    setVerificationError(null);

    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtpCode(digits);
      inputRefs[5].current?.focus();
    }
  };

  // Verify OTP and approve permit/edit request
  const handleVerifyAndApprove = async () => {
    const code = otpCode.join('');

    if (!comments || !comments.trim()) {
      toast.error('Please enter approval comments');
      return;
    }

    if (code.length !== 6) {
      toast.error('Please enter complete 6-digit OTP');
      return;
    }

    setIsVerifying(true);
    setVerificationError(null);

    try {
      if (skipVerification) {
        // Pass OTP code to caller for inline verification (single API call)
        await onSuccess(comments, code);
      } else {
        // Two-step: verify OTP first, then call onSuccess
        const otpResponse = await api.post(`/otp/verify/${otpEndpointPrefix}/${permitId}`, {
          otpCode: code
        });

        if (otpResponse.data.success) {
          toast.success('OTP verified! Processing approval...');
          await onSuccess(comments);
        }
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Invalid OTP';
      setVerificationError(message);
      toast.error(message);

      // Clear OTP inputs on error
      setOtpCode(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Approval Comments */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Approval Comments <span className="text-red-500">*</span>
        </label>
        <textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          rows={3}
          placeholder="Enter your approval comments..."
          disabled={isVerifying || isLoading}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      {/* OTP Section */}
      <div className="border-t pt-4">
        <p className="text-sm text-gray-600 text-center mb-3">
          Enter the 6-digit OTP sent to your registered mobile number
        </p>

        {/* OTP Input Boxes */}
        <div className="flex justify-center gap-2 mb-4" onPaste={handlePaste}>
          {otpCode.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleOTPChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={isVerifying || isGenerating || isLoading}
              className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                verificationError
                  ? 'border-red-300 bg-red-50'
                  : 'border-secondary-300 bg-white'
              } ${(isVerifying || isGenerating || isLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
          ))}
        </div>

        {/* Resend OTP Link with Timer */}
        <div className="flex justify-center mb-4">
          {resendTimer > 0 ? (
            <span className="text-sm text-secondary-500">
              Resend OTP in <strong className="text-secondary-700">{resendTimer}s</strong>
            </span>
          ) : (
            <button
              onClick={resendOTP}
              disabled={isResending || isGenerating || isLoading}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium underline disabled:text-secondary-400 disabled:no-underline disabled:cursor-not-allowed flex items-center"
            >
              {isResending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  Resending...
                </>
              ) : (
                'Resend OTP'
              )}
            </button>
          )}
        </div>

        {/* Error Message */}
        {verificationError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{verificationError}</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={onCancel}
          disabled={isVerifying || isLoading}
          className="btn btn-outline"
        >
          Cancel
        </button>
        <button
          onClick={handleVerifyAndApprove}
          disabled={otpCode.some(d => d === '') || isVerifying || isLoading}
          className="btn btn-primary"
        >
          {isVerifying || isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isVerifying ? 'Verifying...' : 'Approving...'}
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Verify OTP & Approve
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OTPVerificationCompact;
